import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, MessageCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { siteInfo } from '../data/siteData';

const ContactPage = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+971',
    phone: '',
    description: ''
  });
  
  const [errors, setErrors] = useState({
    email: '',
    phone: ''
  });

  // State for selected location on map
  const [selectedLocation, setSelectedLocation] = useState(null);

  // Location data with working Google Maps search URLs
  const locations = [
    {
      id: 1,
      title: 'Dubai - Al Nahda 2',
      address1: 'Show Room 1, Blue Building',
      address2: 'Al Nahda 2, Dubai, UAE',
      searchQuery: 'Blue Building, Al Nahda 2, Dubai, UAE'
    },
    {
      id: 2,
      title: 'Sharjah - Al Nahda',
      address1: 'Shop 1-4, Sun Tower Al Nahda',
      address2: 'Sharjah, UAE',
      searchQuery: 'Sun Tower Al Nahda, Sharjah, UAE'
    },
    {
      id: 3,
      title: 'Dubai - Karama',
      address1: 'Shop 1, Muzoon Building',
      address2: 'Karama, Dubai, UAE',
      searchQuery: 'Muzoon Building, Karama, Dubai, UAE'
    },
    {
      id: 4,
      title: 'Sharjah - Abdullah Al Shaiba Tower',
      address1: 'Shop 11, Street 129',
      address2: 'Abdullah Al Shaiba Tower, Al-Nahda, Sharjah',
      searchQuery: 'Abdullah Al Shaiba Tower, Al Nahda, Sharjah, UAE'
    }
  ];

  // Default map showing all area
  const defaultMapUrl = 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d57697.685289785946!2d55.34503407431642!3d25.298766999999997!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f5fc8c52e2f71%3A0x9c5c2c0e4c7c5c0e!2sAl%20Nahda%2C%20Dubai%20-%20United%20Arab%20Emirates!5e0!3m2!1sen!2sus!4v1647896543210!5m2!1sen!2sus';

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      return 'Email is required';
    }
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }
    return '';
  };

  const validatePhone = (phone) => {
    const cleanPhone = phone.replace(/\D/g, '');
    
    if (!phone) {
      return 'Phone number is required';
    }
    if (!/^\d+$/.test(phone)) {
      return 'Phone number can only contain digits';
    }
    if (cleanPhone.length !== 9) {
      return 'Phone number must be exactly 9 digits';
    }
    return '';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'phone') {
      const digitsOnly = value.replace(/\D/g, '');
      const limitedValue = digitsOnly.slice(0, 9);
      setFormData({ ...formData, [name]: limitedValue });
      
      if (limitedValue) {
        const phoneError = validatePhone(limitedValue);
        setErrors({ ...errors, phone: phoneError });
      } else {
        setErrors({ ...errors, phone: '' });
      }
    } else if (name === 'email') {
      setFormData({ ...formData, [name]: value });
      
      if (value) {
        const emailError = validateEmail(value);
        setErrors({ ...errors, email: emailError });
      } else {
        setErrors({ ...errors, email: '' });
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const emailError = validateEmail(formData.email);
    const phoneError = validatePhone(formData.phone);
    
    setErrors({
      email: emailError,
      phone: phoneError
    });
    
    if (emailError || phoneError) {
      toast({
        title: "Validation Error",
        description: "Please fix the errors before submitting.",
        variant: "destructive"
      });
      return;
    }
    
    try {
      const response = await fetch('/api/send-email.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (!response.ok) {
        throw new Error('Failed to submit');
      }
      
      toast({
        title: "Message Sent Successfully!",
        description: "We'll contact you shortly.",
      });
      
      setFormData({ name: '', email: '', countryCode: '+971', phone: '', description: '' });
      setErrors({ email: '', phone: '' });
      
    } catch (error) {
      console.error('Submission error:', error);
      toast({
        title: "Submission Failed",
        description: "Please try again or contact us directly.",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-black text-white py-20">
        <div className="w-full px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-amber-500 text-sm uppercase tracking-widest mb-4 font-medium">Get In Touch</p>
          <h1 className="text-4xl sm:text-5xl font-bold mb-6">
            Contact Us
          </h1>
          <p className="text-xl text-gray-300 w-full">
            Have a question or ready to book? We're here to help
          </p>
        </div>
      </section>

      {/* Contact Information Cards */}
      <section className="py-12 bg-gray-50">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <a 
              href={`tel:${siteInfo.phone.replace(/\s/g, '')}`}
              className="bg-white p-6 shadow-lg hover:shadow-xl transition-shadow text-center group"
            >
              <div className="w-16 h-16 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                <Phone size={32} className="text-white" />
              </div>
              <h3 className="font-bold text-black mb-2">Call Us</h3>
              <p className="text-amber-500 font-bold">{siteInfo.phone}</p>
            </a>

            <a 
              href={`mailto:${siteInfo.email}`}
              className="bg-white p-6 shadow-lg hover:shadow-xl transition-shadow text-center group"
            >
              <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                <Mail size={32} className="text-white" />
              </div>
              <h3 className="font-bold text-black mb-2">Email</h3>
              <p className="text-blue-500 font-bold text-sm">{siteInfo.email}</p>
            </a>

            <div className="bg-white p-6 shadow-lg text-center">
              <div className="w-16 h-16 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock size={32} className="text-white" />
              </div>
              <h3 className="font-bold text-black mb-2">Hours</h3>
              <p className="text-gray-600 text-sm">{siteInfo.hours}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-20 bg-white">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <h2 className="text-3xl font-bold text-black mb-6">Send Us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 focus:outline-none focus:border-amber-500"
                    placeholder="Your full name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className={`w-full px-4 py-3 border focus:outline-none focus:border-amber-500 ${
                      errors.email ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="your@email.com"
                  />
                  {errors.email && (
                    <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                  <div className="flex gap-2">
                    <select
                      name="countryCode"
                      value={formData.countryCode}
                      onChange={handleChange}
                      className="px-3 py-3 border border-gray-300 focus:outline-none focus:border-amber-500"
                    >
                      <option value="+971">+971 (UAE)</option>
                      <option value="+1">+1 (US)</option>
                      <option value="+44">+44 (UK)</option>
                      <option value="+91">+91 (India)</option>
                      <option value="+92">+92 (Pakistan)</option>
                      <option value="+966">+966 (Saudi)</option>
                    </select>
                    <div className="flex-1">
                      <input
                        type="text"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        maxLength="9"
                        className={`w-full px-4 py-3 border focus:outline-none focus:border-amber-500 ${
                          errors.phone ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="564740785 (9 digits)"
                      />
                    </div>
                  </div>
                  {errors.phone && (
                    <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows="5"
                    className="w-full px-4 py-3 border border-gray-300 focus:outline-none focus:border-amber-500 resize-none"
                    placeholder="Tell us how we can help you..."
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-amber-500 text-black py-4 font-bold uppercase tracking-wider hover:bg-amber-600 hover:text-black transition-colors"
                >
                  Send Message
                </button>
              </form>
            </div>

            {/* Contact Information */}
            <div>
              <h2 className="text-3xl font-bold text-black mb-6">Our Locations</h2>
              <p className="text-gray-600 mb-4 text-sm">📍 Click on any location to view it on the map</p>
              
              {/* Multiple Locations - Now Clickable */}
              <div className="space-y-6 mb-8">
                {locations.map((location) => (
                  <div 
                    key={location.id}
                    onClick={() => setSelectedLocation(location)}
                    className={`flex items-start gap-4 p-4 rounded-lg shadow-sm cursor-pointer transition-all duration-300 ${
                      selectedLocation?.id === location.id 
                        ? 'bg-amber-50 border-2 border-amber-500 shadow-lg' 
                        : 'bg-white hover:bg-amber-50 hover:shadow-md border-2 border-transparent'
                    }`}
                  >
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                      selectedLocation?.id === location.id ? 'bg-amber-600' : 'bg-amber-500'
                    }`}>
                      <MapPin size={24} className="text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-black mb-1">{location.title}</h3>
                      <p className="text-gray-600">{location.address1}</p>
                      <p className="text-gray-600">{location.address2}</p>
                      {selectedLocation?.id === location.id && (
                        <p className="text-amber-600 text-sm font-semibold mt-2">📍 Viewing on map</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Google Maps - Full Width Section - Dynamic Based on Selection */}
      <section className="w-full">
        {selectedLocation && (
          <div className="bg-amber-500 text-black py-3 px-6 text-center">
            <p className="font-semibold">📍 Viewing: {selectedLocation.title}</p>
          </div>
        )}
        <div className="w-full h-[500px] bg-gray-200">
          <iframe
            key={selectedLocation?.id || 'default'}
            src={
              selectedLocation 
                ? `https://maps.google.com/maps?q=${encodeURIComponent(selectedLocation.searchQuery)}&output=embed&z=15`
                : defaultMapUrl
            }
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title={selectedLocation ? `${selectedLocation.title} - LookWell Gents Salon` : "LookWell Gents Salon Locations - Al Nahda Area, Dubai & Sharjah"}
          ></iframe>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
